import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  faDiscord,
  faTiktok,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  EnvelopeIcon,
  // ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import Input from "../components/Input";

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [message, setMessage] = useState<string | null>(null);
  const [messageSuccess, setMessageSuccess] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.firstName) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName) {
      newErrors.lastName = "Last name is required";
    }
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
    }
    if (!formData.message) {
      newErrors.message = "Message is required";
    }
    return newErrors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("https://api.tracknlist.com/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          id: (() =>
            "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) =>
              //@ts-ignore
              (c === "x" ? (Math.random() * 16) | 0 : "r&0x3" | "0x8").toString(
                16
              )
            ))(),
          email: formData.email,
          message: formData.message,
          created_at: +Date.now(),
          timestamp: +Date.now(),
        }),
      });

      if (response.ok) {
        setMessageSuccess(true);
        setMessage("Your message has been sent successfully.");
      } else {
        const errorData = await response.json();
        setMessage(errorData.error || "An error occurred. Please try again.");
      }
    } catch (error) {
      setMessage("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    {
      name: "Discord",
      icon: faDiscord,
      href: "https://www.facebook.com",
      color: "from-indigo-500 to-purple-600",
    },
    {
      name: "X(Twitter)",
      icon: faXTwitter,
      href: "https://x.com/tracknlist",
      color: "from-gray-800 to-gray-900",
    },
    {
      name: "TikTok",
      icon: faTiktok,
      href: "https://www.tiktok.com/@tracknlist",
      color: "from-pink-500 to-red-600",
    },
  ];

  return (
    <div className="relative isolate dark:bg-gray-800 py-12 sm:py-32 overflow-hidden">
      {/* Background decoration */}

      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2 px-6 lg:px-8">
        {/* Left Section - Contact Info */}
        <motion.div
          className="relative pb-12 sm:pb-20 pt-12 sm:pt-32 lg:static lg:px-8 lg:py-48"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
            <svg
              aria-hidden="true"
              className="absolute inset-0 -z-10 h-screen w-screen stroke-gray-200 dark:stroke-gray-700"
            >
              <defs>
                <pattern
                  x="100%"
                  y={-1}
                  id="83fd4e5a-9d52-42fc-97b6-718e5d7ee527"
                  width={200}
                  height={200}
                  patternUnits="userSpaceOnUse"
                >
                  <path d="M130 200V.5M.5 .5H200" fill="none" />
                </pattern>
              </defs>
              <rect
                fill="white"
                className="dark:fill-gray-800"
                width="100%"
                height="100%"
                strokeWidth={0}
              />
              <svg
                x="100%"
                y={-1}
                className="overflow-visible fill-gray-50 dark:fill-gray-800"
              >
                <path d="M-470.5 0h201v201h-201Z" strokeWidth={0} />
              </svg>
              <rect
                fill="url(#83fd4e5a-9d52-42fc-97b6-718e5d7ee527)"
                width="100%"
                height="100%"
                strokeWidth={0}
              />
            </svg>

            <motion.h2
              className="text-center sm:text-left text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              Get in Touch
            </motion.h2>
            <motion.p
              className="mt-3 sm:mt-6 text-center sm:text-left text-lg leading-8 text-gray-600 dark:text-gray-300"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              We're excited to hear from you! Tell us about your experience,
              suggest new features, or just get in touch. Your feedback helps us
              build a better product.
            </motion.p>

            <motion.dl
              className="mt-6 sm:mt-10 space-y-2 sm:space-y-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              {socialLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  className="group"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                  whileHover={{ x: 5 }}
                >
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    <div
                      className={`flex h-10 sm:h-12 w-10 sm:w-12 items-center justify-center rounded-lg bg-gradient-to-br ${link.color} shadow-lg`}
                    >
                      <FontAwesomeIcon
                        icon={link.icon}
                        className="h-5 sm:h-6 w-5 sm:w-6 text-white"
                      />
                    </div>
                    <span className="text-base font-medium text-gray-900 dark:text-white group-hover:text-deepPurple dark:group-hover:text-brightPurple transition-colors">
                      {link.name}
                    </span>
                  </a>
                </motion.div>
              ))}
            </motion.dl>
          </div>
        </motion.div>

        {/* Right Section - Contact Form */}
        <motion.form
          onSubmit={handleSubmit}
          className="px-0 pb-0 pt-0 sm:pt-20 sm:pb-32 lg:px-8 lg:py-48"
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
            {message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-6 p-4 rounded-lg text-sm font-medium ${
                  messageSuccess
                    ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                    : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
                }`}
              >
                {message}
              </motion.div>
            )}

            <motion.div
              className="grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-x-8 sm:gap-y-6 sm:grid-cols-2"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.1,
                  },
                },
              }}
            >
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  label="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={errors.firstName}
                  autoComplete="given-name"
                />
              </motion.div>

              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  label="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={errors.lastName}
                  autoComplete="family-name"
                />
              </motion.div>

              <motion.div
                className="sm:col-span-2"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <Input
                  id="email"
                  name="email"
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                  autoComplete="email"
                />
              </motion.div>

              <motion.div
                className="sm:col-span-2"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
              >
                <Input
                  id="message"
                  name="message"
                  label="Message"
                  value={formData.message}
                  onChange={handleChange}
                  error={errors.message}
                  as="textarea"
                  rows={4}
                />
              </motion.div>
            </motion.div>

            <motion.div
              className="mt-8 flex justify-end"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group relative overflow-hidden rounded-lg bg-gradient-to-r from-softBlue via-brightPurple to-deepPurple px-6 py-3 text-center text-sm font-semibold text-white shadow-lg hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
              >
                <span className="relative z-10 flex items-center justify-center space-x-2">
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <EnvelopeIcon className="h-5 w-5" />
                      <span>Send message</span>
                    </>
                  )}
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-deepPurple via-brightPurple to-softBlue opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={false}
                />
              </motion.button>
            </motion.div>
          </div>
        </motion.form>
      </div>
    </div>
  );
};

export default ContactUs;
